const NOTIFICATION_PREFERENCE_KEY = 'cv_notifications_preference';

const CONFIG = {
    local: {
        vapidPublicKey: 'BGJQFeUBkN4cOFm8CQ61SmWhi_PMKVoVfGhASx8WF7EDOjAkHFEoNvgwz8Y3GOIHuY61HPSRsFR93yabldLmHdg',
        apiBaseUrl: 'http://localhost:7071/api'
    },
    production: {
        vapidPublicKey: 'BGJQFeUBkN4cOFm8CQ61SmWhi_PMKVoVfGhASx8WF7EDOjAkHFEoNvgwz8Y3GOIHuY61HPSRsFR93yabldLmHdg',
        apiBaseUrl: 'https://cv-podcasts-function.azurewebsites.net/api'
    }
};

const resolveConfig = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (hostname === 'localhost') return CONFIG.local;
    return CONFIG.production;
};

export const notificationConfig = resolveConfig();

const normalizeApiBaseUrl = (apiBaseUrl) => {
    if (!apiBaseUrl) {
        return '';
    }

    return apiBaseUrl.replace(/\/$/, '');
};

export const isPushSupported = () => {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
};

export const isRunningAsPWA = () => {
    if (typeof window === 'undefined') return false;
    return (
        window.navigator.standalone === true ||
        window.matchMedia('(display-mode: standalone)').matches
    );
};

export const isSafariIOSWithoutPWA = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    const ua = navigator.userAgent;
    const isIOS =
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isIOS) return false;

    return !isRunningAsPWA();
};

export const registerPodcastServiceWorker = async () => {
    if (!isPushSupported()) {
        return null;
    }

    const registration = await navigator.serviceWorker.register('/podcast-push-sw.js');
    return registration;
};

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
};

const postJson = async (url, payload) => {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        throw new Error(`backend-error:${response.status}`);
    }
};

export const requestNotificationPermission = async () => {
    if (!isPushSupported()) {
        return 'unsupported';
    }

    if (Notification.permission === 'granted') {
        return 'granted';
    }

    return Notification.requestPermission();
};

const getBrowserNotificationPermissionState = async () => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
        return 'unknown';
    }

    try {
        const result = await navigator.permissions.query({ name: 'notifications' });
        return result.state;
    } catch {
        return 'unknown';
    }
};

export const subscribeToPushNotifications = async ({ vapidPublicKey, apiBaseUrl }) => {
    if (!vapidPublicKey) {
        throw new Error('Missing VAPID public key configuration');
    }

    const registration = await registerPodcastServiceWorker();

    if (!registration) {
        throw new Error('Push is not supported in this browser');
    }

    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
    }

    const normalizedApiBaseUrl = normalizeApiBaseUrl(apiBaseUrl);

    if (!normalizedApiBaseUrl) {
        throw new Error('Missing notifier API base URL configuration');
    }

    await postJson(`${normalizedApiBaseUrl}/subscribe`, {
        subscription
    });

    return subscription;
};

export const unsubscribeFromPushNotifications = async ({ apiBaseUrl }) => {
    const registration = await registerPodcastServiceWorker();

    if (!registration) {
        return false;
    }

    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
        return false;
    }

    const normalizedApiBaseUrl = normalizeApiBaseUrl(apiBaseUrl);

    if (normalizedApiBaseUrl) {
        await postJson(`${normalizedApiBaseUrl}/unsubscribe`, {
            endpoint: subscription.endpoint
        });
    }

    await subscription.unsubscribe();

    return true;
};

export const hasActivePushSubscription = async () => {
    if (!isPushSupported()) {
        return false;
    }

    const registration = await registerPodcastServiceWorker();
    if (!registration) {
        return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    return Boolean(subscription);
};

export const saveNotificationPreference = (value) => {
    localStorage.setItem(NOTIFICATION_PREFERENCE_KEY, value);
};

export const getNotificationPreference = () => {
    return localStorage.getItem(NOTIFICATION_PREFERENCE_KEY);
};

export const getNotificationStatus = async () => {
    if (!isPushSupported()) {
        return {
            supported: false,
            permission: 'default',
            enabled: false,
            blocked: true
        };
    }

    const enabled = await hasActivePushSubscription();
    const blocked = Notification.permission === 'denied';

    return {
        supported: true,
        permission: Notification.permission,
        enabled,
        blocked
    };
};

export const enableEpisodeNotifications = async () => {
    if (!notificationConfig.vapidPublicKey) {
        throw new Error('missing-vapid-key');
    }

    if (!notificationConfig.apiBaseUrl) {
        throw new Error('missing-api-url');
    }

    const permission = await requestNotificationPermission();

    if (permission !== 'granted') {
        saveNotificationPreference('denied');

        const blocked = permission === 'denied';

        return {
            supported: true,
            permission,
            enabled: false,
            blocked
        };
    }

    await subscribeToPushNotifications({
        vapidPublicKey: notificationConfig.vapidPublicKey,
        apiBaseUrl: notificationConfig.apiBaseUrl
    });

    saveNotificationPreference('enabled');

    return {
        supported: true,
        permission: 'granted',
        enabled: true,
        blocked: false
    };
};

export const disableEpisodeNotifications = async () => {
    await unsubscribeFromPushNotifications({
        apiBaseUrl: notificationConfig.apiBaseUrl
    });

    saveNotificationPreference('disabled');

    const supported = isPushSupported();
    const permission = supported ? Notification.permission : 'default';

    return {
        supported,
        permission,
        enabled: false,
        blocked: permission === 'denied'
    };
};
